export class RemDtaVkaMapper {
  /**
   * @typedef MapperResult
   * @type {object[]}
   * @property {string} [].propertyId
   * @property {string} [].propertyAddress
   * @property {string} [].billingPeriodStart
   * @property {string} [].billingPeriodEnd
   * @property {string} [].allocations[].provider.id
   * @property {string} [].allocations[].provider.title
   * @property {string} [].allocations[].consumer.id
   * @property {string} [].allocations[].consumer.title
   * @property {string} [].allocations[].proportion
   * @property {string} [].costDistribution[].unit.id
   * @property {string} [].costDistribution[].occupancy.occupant
   * @property {string} [].costDistribution[].occupancy.periodStart
   * @property {string} [].costDistribution[].occupancy.periodEnd
   * @property {string} [].costDistribution[].consumption.id
   * @property {string} [].costDistribution[].consumption.title
   * @property {string} [].costDistribution[].consumption.type
   * @property {string} [].costDistribution[].consumption.proportion
   */

  /**
   * @param {object} object
   * @returns array|MapperResult
   */
  static apply(object) {
    const instance = new this();
    return instance.map(object);
  }

  /**
   * @param object
   * @returns array|MapperResult
   */
  map(object) {
    return this.mapProperty(object);
  }

  /**
   * @param object
   * @returns array|MapperResult
   */
  mapProperty(object) {
    let result = [];
    for (const property of object?.["DTA_REM"]?.[0]?.["Header"]?.[0]?.[
      "Liegenschaft"
    ]) {
      let propertyAddress = property["@_LiegenschaftBezeichnung1"];
      property["@_LiegenschaftBezeichnung2"]
        ? (propertyAddress += " " + property["@_LiegenschaftBezeichnung2"])
        : null;
      propertyAddress += ", " + property["@_LiegenschaftOrt"];

      result.push({
        propertyId: property["@_LiegenschaftID"],
        propertyAddress: propertyAddress,
        billingPeriodStart: property["@_PeriodeNebenkostenAbrechnungVon"],
        billingPeriodEnd: property["@_PeriodeNebenkostenAbrechnungBis"],
        allocations: this.mapAllocations(property),
        costDistribution: this.mapCostDistribution(property)
      });
    }
    return result;
  }

  mapAllocations(property) {
    let result = [];
    for (const mainCostCentre of property?.["Hauptkostenstelle"]) {
      for (const collectionCostCentre of mainCostCentre?.["Sammelkostenstelle"]) { // eslint-disable-line
        result.push({
          provider: {
            id: mainCostCentre["@_HauptkostenstelleID"],
            title: mainCostCentre["@_KostenstelleVonBezeichnung"]
          },
          consumer: {
            id: collectionCostCentre["@_VerbrauchskostenstelleAnID"],
            title: collectionCostCentre["@_VerbrauchskostenstelleAnBezeichnung"]
          },
          proportion: collectionCostCentre["@_Promilleanteil"]
        });
      }
    }
    return result;
  }

  mapCostDistribution(property) {
    let result = [];
    for (const mainCostCentre of property?.["Hauptkostenstelle"] ?? []) {
      for (const building of mainCostCentre?.["Haus"] ?? []) { // eslint-disable-line
        for (const unit of building?.["Objekt"] ?? []) {
          for (const occupation of unit?.["Mietverhaeltnis"] ?? []) {
            for (const consumption of occupation?.["Verbrauchskostenstelle"] ??
              []) {
              result.push({
                type:
                  "[" +
                  consumption["@_KostenstelleID"] +
                  "] " +
                  consumption["@_KostenstelleBezeichnung"],
                unit: building["@_HausID"] + "|" + unit["@_ObjektID"],
                occupant:
                  occupation["@_Mieter1Name"] +
                  " " +
                  occupation["@_Mieter1Vorname"],
                occupancyPeriodStart: occupation["@_BeginnNutzungPeriode"],
                occupancyPeriodEnd: occupation["@_EndeNutzungPeriode"],
                proportion: consumption["@_Promilleanteil"]
              });
            }
          }
        }
      }
    }
    return result
      .sort((a, b) =>
        a.occupancyPeriodStart.localeCompare(b.occupancyPeriodStart)
      )
      .sort((a, b) => a.unit.localeCompare(b.unit))
      .sort((a, b) => a.type.localeCompare(b.type));
  }
}
